import React, { JSX } from "react";
import { Tooltip } from "@/components/common/Tooltip";
import { TruncatedText } from "@/components/common/TruncatedText";
import { useMenuRowProps } from "@/components/common/menuRows";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";

interface MenuItemProps {
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  /// Muted trailing text — the reason an item is unavailable, which a hover
  /// tooltip can't carry inside a menu you are already hovering.
  hint?: React.ReactNode;
  id?: string;
  className?: string;
}

export function MenuItem({
  Icon,
  label,
  onClick,
  disabled,
  hint,
  id,
  className,
}: MenuItemProps): JSX.Element {
  return (
    <button
      type="button"
      {...useMenuRowProps()}
      // `aria-disabled` rather than `disabled`: an unavailable action stays
      // focusable, so arrowing through the menu still shows it exists.
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      className={`menu-item flex items-center gap-2 text-fg-regular whitespace-nowrap${className ? ` ${className}` : ""}`}
      id={id}
      style={{
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <Icon className="icon-sm" />
      <TruncatedText className="grow text-left">{label}</TruncatedText>
      {hint && (
        <span className="text-fg-muted-regular-small ml-3 shrink-0 flex items-center">
          {hint}
        </span>
      )}
    </button>
  );
}

interface MenuGroupLabelProps {
  label: string;
  onNavigate?: (e: React.MouseEvent) => void;
}

export function MenuGroupLabel({
  label,
  onNavigate,
}: MenuGroupLabelProps): JSX.Element {
  if (onNavigate) {
    return (
      <div className="px-3 py-2 select-none">
        <Tooltip title="Select table">
          <button
            onClick={onNavigate}
            className="tab-option tab-option-active border-[0.5px] border-border cursor-pointer"
          >
            <span className="text-fg-muted-regular">{label}</span>
            <ChevronRightIcon className="icon-xs text-fg-muted" />
          </button>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="px-4 py-2 select-none">
      <span className="text-fg-muted-medium">{label}</span>
    </div>
  );
}

export function MenuDivider(): JSX.Element {
  return <div role="separator" className="border-t border-border mx-2 my-1" />;
}
